import { getTaxSettingForPaymentMethod, roundCurrency } from "./bookingCalculations";
import { ADDITIONAL_PIZZA_PRICE, findPartyPackage, PARTY_PACKAGES } from "../constants/packageDefaults";
import { calculateGuestCharges } from "./bookingPricing";

export const GENERATOR_PACKAGES = PARTY_PACKAGES;

export const INVOICE_ADD_ONS = [
  { name: "+30 mins of playtime", price: 300, variable: false },
  { name: "+1 hr of playtime", price: 400, variable: false },
  { name: "Cotton Candy Station", price: 200, variable: false },
  { name: "Sparkly Painting & Glitter Tattoos", price: 150, variable: false },
  { name: "Friendship Bracelet-Making", price: 200, variable: false },
  { name: "Balloon Decorations", price: 0, variable: true },
  { name: "Themed Party Character Appearance", price: 250, variable: false },
  { name: "Character Cutouts", price: 50, variable: false },
  { name: "Arch Backdrops", price: 150, variable: false },
  { name: "Professional Photography", price: 150, variable: false },
  { name: "Themed Marshmallow Treats (20 count)", price: 90, variable: false },
  { name: "Party Goodie Bags", price: 10, variable: true },
  { name: "Goodie Bag Display Stand", price: 150, variable: false },
  { name: "Large One-Topping Pizza", price: ADDITIONAL_PIZZA_PRICE, variable: false },
];

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const datePart = (value) => {
  const raw = String(value || "").trim();
  return raw.includes("T") ? raw.split("T")[0] : raw;
};

const timePart = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return (raw.includes("T") ? raw.split("T")[1] : raw).slice(0, 5);
};

const timeRange = (startTime, endTime) =>
  [timePart(startTime), timePart(endTime)].filter(Boolean).join(" - ");

const customerName = (booking) =>
  [booking?.firstName, booking?.lastName].filter(Boolean).join(" ").trim();

export const findGeneratorPackage = findPartyPackage;

// Never substitute current defaults for an existing booking without saved
// pricing. Its original all-in total must be reviewed and entered by staff.
const savedPackage = (booking) => booking?.pricingSnapshot?.package
  ? { ...booking.pricingSnapshot.package } : null;

const historicalPackageName = (booking) =>
  findPartyPackage(booking?.packageType)?.name || booking?.packageType || "Booking";

const bookingNotes = (booking, selectedPackage) => {
  const lines = [];
  if (booking?.message) lines.push(String(booking.message).trim());
  if (booking?.numberOfKids !== undefined && booking.numberOfKids !== "") {
    lines.push(`Number of kids: ${booking.numberOfKids}`);
  }
  if (booking?.numberOfGuests !== undefined && booking.numberOfGuests !== "") {
    lines.push(`Number of guests: ${booking.numberOfGuests}`);
  }
  if (booking?.packageType && !selectedPackage) {
    lines.push(`Calendar package: ${booking.packageType}`);
  }
  return lines.filter(Boolean).join("\n");
};

// This adapter is intentionally read-only and uses the established Event Calendar
// document keys. Missing generator-only values stay blank for manual review.
export const mapCalendarBookingToInvoicePrefill = (booking) => {
  const selectedPackage = savedPackage(booking);
  const guests = calculateGuestCharges(selectedPackage, booking?.numberOfKids, booking?.numberOfGuests);

  return {
    sourceEventId: booking?.id || "",
    customerName: customerName(booking),
    childName: "",
    customerPhone: booking?.phoneNumber || "",
    customerEmail: booking?.email || "",
    eventDate: datePart(booking?.startTime),
    eventStartTime: timePart(booking?.startTime),
    eventEndTime: timePart(booking?.endTime),
    selectedPackage,
    pricingNeedsReview: !selectedPackage,
    historicalPackageName: historicalPackageName(booking),
    additionalKids: selectedPackage ? guests.additionalKids : 0,
    additionalAdults: selectedPackage ? guests.additionalAdults : 0,
    addOnSelections: [],
    customItems: [],
    discount: 0,
    depositPaid: roundCurrency(
      Math.max(0, toNumber(booking?.depositAmount, 0)) +
      Math.max(0, toNumber(booking?.totalPaidAmount, 0))
    ),
    notes: bookingNotes(booking, selectedPackage),
  };
};

export const mapCalendarBookingToDepositPrefill = (booking) => {
  const selectedPackage = savedPackage(booking);
  const paymentMethod = booking?.paymentMethod || "";

  return {
    sourceEventId: booking?.id || "",
    customerName: customerName(booking),
    childName: "",
    customerPhone: booking?.phoneNumber || "",
    customerEmail: booking?.email || "",
    eventDate: datePart(booking?.startTime),
    eventTime: timeRange(booking?.startTime, booking?.endTime),
    selectedPackage,
    pricingNeedsReview: !selectedPackage,
    historicalPackageName: historicalPackageName(booking),
    additionalCharges: selectedPackage
      ? calculateGuestCharges(selectedPackage, booking?.numberOfKids, booking?.numberOfGuests).total : 0,
    discount: 0,
    depositAmount: Math.max(0, toNumber(booking?.depositAmount, 0)),
    additionalPaymentAmount: Math.max(
      0,
      toNumber(booking?.totalPaidAmount, 0)
    ),
    depositPaidDate: "",
    paymentMethod,
    applyTax: getTaxSettingForPaymentMethod(paymentMethod),
    paidInFull: booking?.paidInFull === true,
    notes: bookingNotes(booking, selectedPackage),
  };
};
